import { Button, Stack, Text, useDisclosure } from '@chakra-ui/react'
import { faHand } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useCallback } from 'react'

import { useUser } from '@/client/context/user'
import useAPIWrapper from '@/client/hooks/useAPIWrapper'
import RPC from '@/client/rpc'
import isUserAgeAssured from '@/client/services/ageAssurance/isUserAgeAssured'
import shouldEnforceAgeAssurance from '@/client/services/ageAssurance/shouldEnforceAgeAssurance'
import { hasUserRole } from '@/client/services/user'

import AgeAssuranceModal from '@/components/AgeAssurance/AgeAssuranceModal'
import AgeAssurancePolicyNotice from '@/components/AgeAssurance/AgeAssurancePolicyNotice'
import type { PostDetails } from '@/components/Community/Post/types'

import LearnerRequestLiveHelpModal from './LearnerRequestLiveHelpModal'

export default function AuthorRequestLiveHelpBanner({
  post,
  postIsFullView,
}: {
  post: PostDetails
  postIsFullView: boolean
}): JSX.Element | null {
  const user = useUser()

  const shouldEnforceAssurance = shouldEnforceAgeAssurance({ userId: user.id })
  const shouldStartAgeAssurance = shouldEnforceAssurance && !isUserAgeAssured(user)

  const {
    onOpen: onOpenConfirmationModal,
    onClose: onCloseConfirmationModal,
    isOpen: isConfirmationModalOpen,
  } = useDisclosure()

  const {
    onOpen: onOpenAgeAssuranceModal,
    onClose: onCloseAgeAssuranceModal,
    isOpen: isAgeAssuranceModalOpen,
  } = useDisclosure()

  const [requestLiveHelp, isRequestingLiveHelp] = useAPIWrapper(
    async () => {
      await RPC.requestLiveHelp({
        postId: post.id,
      })
    },
    [post.id],
    'Request Live Help for Post'
  )

  const onButtonClick = useCallback(() => {
    if (shouldStartAgeAssurance) {
      onOpenAgeAssuranceModal()
    } else {
      onOpenConfirmationModal()
    }
  }, [shouldStartAgeAssurance, onOpenAgeAssuranceModal, onOpenConfirmationModal])

  return shouldEnforceAssurance && hasUserRole(user, 'user:age_assurance:failed') ? null : (
    <>
      <Stack
        direction={postIsFullView ? { base: 'column', md: 'row' } : 'column'}
        align={postIsFullView ? { base: 'left', md: 'center' } : 'left'}
        justify="space-between"
        width="100%"
        p="16px"
        bgColor="bg.muted"
        borderRadius="8px"
      >
        <Stack spacing="4px">
          <Text fontWeight="bold">Interested in receiving help over Zoom?</Text>
          <Text>
            {shouldStartAgeAssurance ? (
              <AgeAssurancePolicyNotice />
            ) : (
              <>
                Join a certified tutor for a Zoom session to get more in-depth help with your
                question. Request Zoom help, and we&apos;ll let you know when a tutor is available.
              </>
            )}
          </Text>
        </Stack>
        <Button
          onClick={onButtonClick}
          flexShrink={0}
          leftIcon={<FontAwesomeIcon icon={faHand} />}
          isLoading={isRequestingLiveHelp}
          variant={!shouldStartAgeAssurance ? 'solid' : 'outline'}
        >
          {shouldStartAgeAssurance ? 'Verify Age' : 'Request Zoom Help'}
        </Button>
      </Stack>
      <LearnerRequestLiveHelpModal
        open={isConfirmationModalOpen}
        onSubmit={requestLiveHelp}
        onClose={onCloseConfirmationModal}
        isLoading={isRequestingLiveHelp}
      />
      <AgeAssuranceModal isOpen={isAgeAssuranceModalOpen} onClose={onCloseAgeAssuranceModal} />
    </>
  )
}
